import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wrench, X as XIcon } from 'lucide-react';
import { API_BASE } from '../../utils/api-base';
import { getWebSocketClient } from '../../utils/websocket-client';

interface MaintenanceWindow {
  id: string;
  title: string;
  description: string | null;
  startTime: string;
  endTime: string;
  status: string;
}

interface MaintenanceBannerProps {
  clientId: string;
  token: string;
}

export function MaintenanceBanner({ clientId, token }: MaintenanceBannerProps) {
  const [windows, setWindows] = useState<MaintenanceWindow[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!clientId || !token) return;
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/clients/${clientId}/maintenance-windows`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await res.json();
        if (res.ok && payload?.success) setWindows(payload.windows || []);
      } catch (_) { /* non-fatal — banner just stays empty until next poll/event */ }
    };
    load();

    const ws = getWebSocketClient(token, { clientId });
    const unsubscribe = ws.on('maintenance_update', (data: any) => {
      const action = data?.action;
      const win = data?.window;
      if (!win?.id) return;
      setWindows((prev) => {
        if (action === 'cancelled') return prev.filter((w) => w.id !== win.id);
        const others = prev.filter((w) => w.id !== win.id);
        return [{ ...win }, ...others];
      });
    });

    return () => unsubscribe();
  }, [clientId, token]);

  const now = Date.now();
  const active = useMemo(
    () => windows.find((w) => new Date(w.startTime).getTime() <= now && new Date(w.endTime).getTime() >= now),
    [windows, now]
  );
  const upcoming = useMemo(
    () => windows
      .filter((w) => new Date(w.startTime).getTime() > now)
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())[0],
    [windows, now]
  );
  const headline = active || upcoming;

  const formatRange = (w: MaintenanceWindow) => {
    const start = new Date(w.startTime);
    const end = new Date(w.endTime);
    const sameDay = start.toDateString() === end.toDateString();
    const dateOpts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    const timeOpts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };
    if (sameDay) {
      return `${start.toLocaleDateString(undefined, dateOpts)}, ${start.toLocaleTimeString(undefined, timeOpts)} - ${end.toLocaleTimeString(undefined, timeOpts)}`;
    }
    return `${start.toLocaleDateString(undefined, dateOpts)} ${start.toLocaleTimeString(undefined, timeOpts)} - ${end.toLocaleDateString(undefined, dateOpts)} ${end.toLocaleTimeString(undefined, timeOpts)}`;
  };

  return (
    <AnimatePresence>
      {headline && !dismissedIds.has(headline.id) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className={
            active
              ? 'bg-orange-50 dark:bg-orange-950/30 border-b-2 border-orange-500 px-6 py-3'
              : 'bg-blue-50 dark:bg-blue-950/30 border-b-2 border-blue-500 px-6 py-3'
          }
        >
          <div className="flex items-center gap-3">
            <Wrench className={active ? 'h-5 w-5 text-orange-600 dark:text-orange-500 shrink-0' : 'h-5 w-5 text-blue-600 dark:text-blue-500 shrink-0'} />
            <div className="flex-1">
              <p className={active ? 'font-semibold text-orange-900 dark:text-orange-200' : 'font-semibold text-blue-900 dark:text-blue-200'}>
                {active ? 'Maintenance in progress: ' : 'Scheduled maintenance: '}{headline.title}
              </p>
              <p className={active ? 'text-sm text-orange-800 dark:text-orange-300' : 'text-sm text-blue-800 dark:text-blue-300'}>
                {formatRange(headline)}
                {headline.description ? ` — ${headline.description}` : ''}
              </p>
            </div>
            <button
              onClick={() => setDismissedIds((prev) => new Set(prev).add(headline.id))}
              className={active ? 'p-1 rounded hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors shrink-0' : 'p-1 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors shrink-0'}
              aria-label="Dismiss maintenance notice"
            >
              <XIcon className={active ? 'h-4 w-4 text-orange-700 dark:text-orange-400' : 'h-4 w-4 text-blue-700 dark:text-blue-400'} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
